import { Platform, ViewStyle } from 'react-native';

/**
 * Shadow style properties for React Native
 */
export interface ShadowStyle {
    shadowColor?: string;
    shadowOffset?: { width: number; height: number };
    shadowOpacity?: number;
    shadowRadius?: number;
    elevation?: number; // Android
}

/**
 * Convert shadow properties to web-compatible boxShadow
 * On native platforms, returns the original shadow properties
 * On web, converts to boxShadow CSS property
 */
export function createShadowStyle(shadow: ShadowStyle): ViewStyle {
    if (Platform.OS === 'web') {
        // Convert shadow properties to boxShadow for web
        const { shadowColor = '#000', shadowOffset, shadowOpacity = 0, shadowRadius = 0 } = shadow;
        
        if (shadowOffset && shadowRadius > 0) {
            const { width = 0, height = 0 } = shadowOffset;
            const opacity = shadowOpacity;
            const color = shadowColor || '#000';
            
            // Convert hex color to rgba if needed
            let rgbaColor = color;
            if (color.startsWith('#')) {
                const hex = color.slice(1);
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                const a = opacity;
                rgbaColor = `rgba(${r}, ${g}, ${b}, ${a})`;
            } else if (color.startsWith('rgba')) {
                // Extract opacity from rgba and multiply with shadowOpacity
                const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
                if (rgbaMatch) {
                    const [, r, g, b, a = '1'] = rgbaMatch;
                    rgbaColor = `rgba(${r}, ${g}, ${b}, ${parseFloat(a) * opacity})`;
                }
            }
            
            return {
                boxShadow: `${width}px ${height}px ${shadowRadius}px ${rgbaColor}`,
            };
        }
        
        return {};
    }
    
    // Native platforms: return shadow properties as-is
    return shadow as ViewStyle;
}

/**
 * Helper to create shadow styles that work on all platforms
 * Usage:
 *   const shadowStyle = getShadowStyle({
 *     shadowColor: '#000',
 *     shadowOffset: { width: 0, height: 2 },
 *     shadowOpacity: 0.25,
 *     shadowRadius: 4,
 *     elevation: 3, // Android
 *   });
 */
export function getShadowStyle(shadow: ShadowStyle): ViewStyle {
    const webShadow = createShadowStyle(shadow);
    
    if (Platform.OS === 'web') {
        return webShadow;
    }
    
    // Native: combine shadow properties with elevation
    return {
        ...shadow,
        ...webShadow,
    } as ViewStyle;
}

